import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { LayoutGrid, CheckCircle2, XCircle, ChevronDown, ChevronRight, ShieldCheck, FlaskConical, Copy } from "lucide-react";

const SURFACES = [
  ["on_homepage", "Home"], ["in_marketplace", "Market"], ["in_dropshipping", "Dropship"],
  ["in_wholesale", "Wholesale"], ["in_pro_affiliate", "Pro/Aff"], ["in_seller_library", "Seller Lib"],
  ["in_nav", "Nav"], ["in_mobile", "Mobile"], ["featured", "Featured"], ["active", "Active"],
  ["hidden", "Hidden"], ["coming_soon", "Coming Soon"],
];

const Chip = ({ on, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-2 py-1 text-[11px] font-bold uppercase tracking-wide border rounded-sm transition-colors ${on ? "bg-taven-black text-white border-taven-black" : "bg-white text-muted-foreground border-black/15 hover:border-taven-gold"}`}>
    {label}
  </button>
);

const MappingReport = ({ onApplied }) => {
  const [r, setR] = useState(null);
  const [applying, setApplying] = useState(false);
  useEffect(() => { adminClient().get("/admin/cj/collection-mapping-report").then(({ data }) => setR(data)).catch(() => {}); }, []);
  if (!r) return null;
  const miss = r.missing_collection || {};
  const apply = async () => {
    setApplying(true);
    try { const { data } = await adminClient().post("/admin/cj/collection-mapping/apply"); toast.success(`Assigned ${data.assigned}, skipped ${data.skipped_unmappable}`); onApplied?.(); }
    catch { toast.error("Apply failed"); } finally { setApplying(false); }
  };
  return (
    <div className="border border-black/10 bg-taven-gray/30 p-4 mb-6" data-testid="mapping-report">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Canonical Mapping — read-only, no Shopify writes</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><span className="text-2xl font-black font-display">{r.canonical_count}</span><p className="text-xs text-muted-foreground">Canonical collections</p></div>
        <div><span className="text-2xl font-black font-display">{r.duplicate_pairs_count}</span><p className="text-xs text-muted-foreground">Duplicate pairs handled</p></div>
        <div><span className="text-2xl font-black font-display">{miss.total}</span><p className="text-xs text-muted-foreground">Products missing collection</p></div>
        <div className="flex items-center gap-1.5">{r.shopify?.readable ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-amber-500" />}<span className="text-xs">{r.shopify?.readable ? `${r.shopify.collections.length} Shopify collections read` : "Shopify not readable yet"}</span></div>
      </div>
      {r.shopify?.note && <p className="text-[11px] text-amber-700 mt-2">{r.shopify.note}</p>}
      {miss.total > 0 && (
        <div className="mt-3 flex items-center gap-3">
          <button onClick={apply} disabled={applying} className="bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-50" data-testid="apply-mapping-btn">{applying ? "Applying…" : `Approve & Assign (${miss.assignable_in_sample}+ mappable)`}</button>
          <span className="text-[11px] text-muted-foreground">Internal catalog only — no Shopify changes, no publishing.</span>
        </div>
      )}
      {miss.total === 0 && <p className="text-[11px] text-green-700 mt-2">Every active product already belongs to a canonical collection. No assignment needed.</p>}
    </div>
  );
};

const PhaseATools = () => {
  const [cat, setCat] = useState(null);
  const [dedup, setDedup] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminClient().get("/admin/cj/dedup-report").then(({ data }) => setDedup(data)).catch(() => {}); }, []);
  const runCat = async () => {
    setBusy(true);
    try { const { data } = await adminClient().post("/admin/cj/category-mapping-test"); setCat(data); toast.success("Controlled mapping test complete (no writes)"); }
    catch { toast.error("Test failed"); } finally { setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4 mb-6">
      <div className="border border-black/10 bg-white p-4" data-testid="category-test-panel">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><FlaskConical className="w-3.5 h-3.5" /> Controlled CJ Category Mapping Test</p>
        <button onClick={runCat} disabled={busy} className="bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-50" data-testid="run-category-test-btn">{busy ? "Running…" : "Run Mapping Test"}</button>
        {cat && (
          <div className="mt-3 text-xs space-y-1" data-testid="category-test-result">
            <p><strong>CJ category:</strong> {cat.cj_category}</p>
            <p><strong>→ Canonical:</strong> {cat.canonical_target_name || "—"} ({cat.canonical_target_handle || "unmapped"})</p>
            <p><strong>Products in category:</strong> {cat.products_in_cj_category}</p>
            <p className="text-green-700">{cat.writes}</p>
          </div>
        )}
      </div>
      <div className="border border-black/10 bg-white p-4" data-testid="dedup-panel">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Duplicate Prevention Report</p>
        {!dedup ? <p className="text-xs text-muted-foreground">Loading…</p> : (
          <div className="text-xs space-y-1" data-testid="dedup-result">
            <p className={`font-bold uppercase ${dedup.status === "clean" ? "text-green-700" : "text-amber-700"}`}>{dedup.status} · {dedup.scope}</p>
            <p>CJ product ID dupes: {dedup.primary_keys.cj_product_id.duplicate_groups}</p>
            <p>Supplier SKU dupes: {dedup.primary_keys.supplier_sku.duplicate_groups}</p>
            <p>Shopify product ID dupes: {dedup.primary_keys.shopify_product_id.duplicate_groups}</p>
            <p className="text-muted-foreground">Archived shadow copies: {dedup.archived_shadow_copies.count} (dedup working)</p>
            <p className="text-muted-foreground">Title used for dedup: {String(dedup.title_used_for_dedup)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminCollectionManager() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => adminClient().get("/admin/cj/collection-display").then(({ data }) => setData(data.collections)).catch(() => setData([]));
  useEffect(() => { load(); }, []);

  const save = async (handle, patch) => {
    try {
      const { data: saved } = await adminClient().put(`/admin/cj/collection-display/${handle}`, patch);
      setData((d) => d.map((c) => (c.handle === handle ? { ...c, ...saved } : c)));
    } catch { toast.error("Could not save"); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading collections…</p>;

  return (
    <div data-testid="admin-collection-manager">
      <div className="flex items-center gap-2 mb-1"><LayoutGrid className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Collection Display Manager</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Control exactly where each canonical collection appears. Reads & maps live collections without ever creating, renaming, or deleting anything on Shopify. Publishing stays disabled.</p>

      <MappingReport onApplied={load} />
      <PhaseATools />

      <div className="border border-black/10 bg-white overflow-x-auto" data-testid="collection-display-table">
        <table className="w-full text-sm">
          <thead className="bg-taven-gray/60 text-left"><tr>
            <th className="px-3 py-2 font-bold">Collection</th>
            <th className="px-3 py-2 font-bold">Products</th>
            <th className="px-3 py-2 font-bold">Display Surfaces</th>
            <th className="px-3 py-2 font-bold">Order</th>
            <th className="px-3 py-2 font-bold"></th>
          </tr></thead>
          <tbody className="divide-y divide-black/5">
            {data.map((c) => (
              <React.Fragment key={c.handle}>
                <tr data-testid={`col-row-${c.handle}`}>
                  <td className="px-3 py-2 font-semibold">{c.name || c.handle}<div className="text-[10px] text-muted-foreground font-normal">{c.handle}</div></td>
                  <td className="px-3 py-2">{c.active_products}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {SURFACES.map(([key, label]) => (
                        <Chip key={key} on={c[key]} label={label} onClick={() => save(c.handle, { [key]: !c[key] })} testid={`chip-${c.handle}-${key}`} />
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" defaultValue={c.display_order} onBlur={(e) => save(c.handle, { display_order: Number(e.target.value) || 0 })}
                      className="w-16 border border-black/15 px-2 py-1 text-sm outline-none focus:border-taven-gold" data-testid={`order-${c.handle}`} />
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => setOpen(open === c.handle ? null : c.handle)} className="text-muted-foreground hover:text-taven-black" data-testid={`expand-${c.handle}`}>
                      {open === c.handle ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </td>
                </tr>
                {open === c.handle && (
                  <tr className="bg-taven-gray/20"><td colSpan={5} className="px-3 py-3">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <label className="text-xs">SEO Title<input defaultValue={c.seo_title} onBlur={(e) => save(c.handle, { seo_title: e.target.value })} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold" data-testid={`seo-title-${c.handle}`} /></label>
                      <label className="text-xs">SEO Description<input defaultValue={c.seo_description} onBlur={(e) => save(c.handle, { seo_description: e.target.value })} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold" data-testid={`seo-desc-${c.handle}`} /></label>
                      <label className="text-xs">Banner Image URL<input defaultValue={c.banner} onBlur={(e) => save(c.handle, { banner: e.target.value })} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold" data-testid={`banner-${c.handle}`} /></label>
                      <div className="flex gap-2">
                        <label className="text-xs flex-1">Product Limit<input type="number" defaultValue={c.product_limit} onBlur={(e) => save(c.handle, { product_limit: Number(e.target.value) || 60 })} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold" data-testid={`limit-${c.handle}`} /></label>
                        <label className="text-xs flex-1">Rotation
                          <select defaultValue={c.rotation} onChange={(e) => save(c.handle, { rotation: e.target.value })} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold" data-testid={`rotation-${c.handle}`}>
                            <option value="auto">Auto</option><option value="manual">Manual</option>
                          </select>
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <label className="text-xs flex-1">Min Approved Products<input type="number" defaultValue={c.min_approved_products} onBlur={(e) => save(c.handle, { min_approved_products: Number(e.target.value) || 0 })} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold" data-testid={`minapp-${c.handle}`} /></label>
                        <label className="text-xs flex-1">Seller Eligibility
                          <select defaultValue={c.seller_eligibility} onChange={(e) => save(c.handle, { seller_eligibility: e.target.value })} className="block w-full mt-1 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold" data-testid={`seller-elig-${c.handle}`}>
                            <option value="all">All sellers</option><option value="approved_only">Approved only</option><option value="none">None</option>
                          </select>
                        </label>
                      </div>
                      <div className="flex gap-4 items-center">
                        <label className="text-xs flex items-center gap-1.5"><input type="checkbox" defaultChecked={c.auto_fill} onChange={(e) => save(c.handle, { auto_fill: e.target.checked })} data-testid={`autofill-${c.handle}`} /> Auto-fill</label>
                        <label className="text-xs flex items-center gap-1.5"><input type="checkbox" defaultChecked={c.approval_required} onChange={(e) => save(c.handle, { approval_required: e.target.checked })} data-testid={`approval-${c.handle}`} /> Approval required</label>
                      </div>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
